/**
 * Learning Management System API Service
 * Handles all LMS-related API calls
 */

import api from './api.js'

class LMSAPIService {
  constructor() {
    this.baseURL = '/lms'
  }

  // Categories
  async getCategories() {
    return api.get(`${this.baseURL}/categories/`)
  }

  async getCategory(id) {
    return api.get(`${this.baseURL}/categories/${id}/`)
  }

  async createCategory(data) {
    return api.post(`${this.baseURL}/categories/`, data)
  }

  async updateCategory(id, data) {
    return api.put(`${this.baseURL}/categories/${id}/`, data)
  }

  async deleteCategory(id) {
    return api.delete(`${this.baseURL}/categories/${id}/`)
  }

  // Courses
  async getCourses(params = {}) {
    return api.get(`${this.baseURL}/courses/`, { params })
  }

  async getFeaturedCourses(params = {}) {
    return api.get(`${this.baseURL}/courses/featured/`, { params })
  }

  async getCourse(id) {
    return api.get(`${this.baseURL}/courses/${id}/`)
  }

  async createCourse(data) {
    return api.post(`${this.baseURL}/courses/`, data)
  }

  async updateCourse(id, data) {
    return api.put(`${this.baseURL}/courses/${id}/`, data)
  }

  async deleteCourse(id) {
    return api.delete(`${this.baseURL}/courses/${id}/`)
  }

  // Course enrollment
  async enrollInCourse(courseId) {
    return api.post(`${this.baseURL}/courses/${courseId}/enroll/`)
  }

  async unenrollFromCourse(courseId) {
    return api.delete(`${this.baseURL}/courses/${courseId}/unenroll/`)
  }

  async getCourseProgress(courseId) {
    return api.get(`${this.baseURL}/courses/${courseId}/progress/`)
  }

  async updateVideoProgress(courseId, progressData) {
    return api.post(`${this.baseURL}/courses/${courseId}/update_progress/`, progressData)
  }

  // Course reviews
  async getCourseReviews(courseId) {
    return api.get(`${this.baseURL}/courses/${courseId}/reviews/`)
  }

  async createCourseReview(courseId, reviewData) {
    return api.post(`${this.baseURL}/courses/${courseId}/reviews/`, reviewData)
  }

  // Videos
  async getVideos(params = {}) {
    return api.get(`${this.baseURL}/videos/`, { params })
  }

  async getVideo(id) {
    return api.get(`${this.baseURL}/videos/${id}/`)
  }

  async createVideo(data) {
    const formData = new FormData()
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key])
      }
    })
    return api.post(`${this.baseURL}/videos/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  async updateVideo(id, data) {
    return api.put(`${this.baseURL}/videos/${id}/`, data)
  }

  async deleteVideo(id) {
    return api.delete(`${this.baseURL}/videos/${id}/`)
  }

  async addVideoTimestamp(videoId, timestampData) {
    return api.post(`${this.baseURL}/videos/${videoId}/add_timestamp/`, timestampData)
  }

  async addVideoResource(videoId, resourceData) {
    return api.post(`${this.baseURL}/videos/${videoId}/add_resource/`, resourceData)
  }

  // Enrollments
  async getEnrollments(params = {}) {
    return api.get(`${this.baseURL}/enrollments/`, { params })
  }

  async getEnrollment(id) {
    return api.get(`${this.baseURL}/enrollments/${id}/`)
  }

  // Learning Paths
  async getLearningPaths(params = {}) {
    return api.get(`${this.baseURL}/learning-paths/`, { params })
  }

  async getLearningPath(id) {
    return api.get(`${this.baseURL}/learning-paths/${id}/`)
  }

  async createLearningPath(data) {
    return api.post(`${this.baseURL}/learning-paths/`, data)
  }

  async updateLearningPath(id, data) {
    return api.put(`${this.baseURL}/learning-paths/${id}/`, data)
  }

  async deleteLearningPath(id) {
    return api.delete(`${this.baseURL}/learning-paths/${id}/`)
  }

  // Dashboard and Analytics
  async getDashboardOverview() {
    return api.get(`${this.baseURL}/dashboard/overview/`)
  }

  async getAnalytics() {
    return api.get(`${this.baseURL}/dashboard/analytics/`)
  }

  // Search and filtering helpers
  async searchCourses(query, filters = {}) {
    const params = {
      search: query,
      ...filters
    }
    return this.getCourses(params)
  }

  async getCoursesByCategory(categoryId) {
    return this.getCourses({ category: categoryId })
  }

  async getEnrolledCourses() {
    return this.getCourses({ enrolled_only: true })
  }

  async getLiveCourses() {
    return this.getCourses({ course_type: 'LIVE_EVENT' })
  }

  async getOnDemandCourses() {
    return this.getCourses({ course_type: 'ON_DEMAND' })
  }

  // Unified search across courses and research (with veterinary optimization)
  async unifiedSearch(query, searchType = 'all', maxResults = 20, options = {}) {
    const params = {
      q: query,
      type: searchType,
      max_results: maxResults,
      ...options
    }
    
    // Add veterinary filter options
    if (options.species && Array.isArray(options.species)) {
      params.species = options.species.join(',')
    }
    if (options.englishOnly !== undefined) {
      params.english_only = options.englishOnly
    }
    if (options.excludeHumans !== undefined) {
      params.exclude_humans = options.excludeHumans
    }
    if (options.dateRangeYears) {
      params.date_range_years = options.dateRangeYears
    }
    // Add database IDs filter (for selecting specific databases to search)
    if (options.database_ids) {
      params.database_ids = options.database_ids
    }
    
    return api.get(`${this.baseURL}/search/unified_search/`, { params })
  }

  // Get available species filter options
  async getSpeciesOptions() {
    return api.get(`${this.baseURL}/search/species_options/`)
  }

  // Get available NCBI databases
  async getNcbiDatabases() {
    return api.get(`${this.baseURL}/search/ncbi_databases/`)
  }

  submitConsultFeedback(data) {
    return api.post('/vet-consult/feedback/', data)
  }

  // ============================================================================
  // Saved Searches
  // ============================================================================

  async getSavedSearches() {
    return api.get(`${this.baseURL}/saved-searches/`)
  }

  async getSavedSearch(id) {
    return api.get(`${this.baseURL}/saved-searches/${id}/`)
  }

  async createSavedSearch(data) {
    return api.post(`${this.baseURL}/saved-searches/`, data)
  }

  async updateSavedSearch(id, data) {
    return api.patch(`${this.baseURL}/saved-searches/${id}/`, data)
  }

  async deleteSavedSearch(id) {
    return api.delete(`${this.baseURL}/saved-searches/${id}/`)
  }

  async executeSavedSearch(id) {
    return api.post(`${this.baseURL}/saved-searches/${id}/execute/`)
  }

  async getRecentSavedSearches() {
    return api.get(`${this.baseURL}/saved-searches/recent/`)
  }

  async getPopularSavedSearches() {
    return api.get(`${this.baseURL}/saved-searches/popular/`)
  }

  // ============================================================================
  // Saved Resources (Research Library)
  // ============================================================================

  async getSavedResources(params = {}) {
    return api.get(`${this.baseURL}/saved-resources/`, { params })
  }

  async getSavedResource(id) {
    return api.get(`${this.baseURL}/saved-resources/${id}/`)
  }

  async saveResourceFromSearch(articleData) {
    return api.post(`${this.baseURL}/saved-resources/save_from_search/`, articleData)
  }

  async updateSavedResource(id, data) {
    return api.patch(`${this.baseURL}/saved-resources/${id}/`, data)
  }

  async deleteSavedResource(id) {
    return api.delete(`${this.baseURL}/saved-resources/${id}/`)
  }

  async toggleResourceFavorite(id) {
    return api.post(`${this.baseURL}/saved-resources/${id}/toggle_favorite/`)
  }

  async tagResourceToCourse(resourceId, courseIds) {
    return api.post(`${this.baseURL}/saved-resources/${resourceId}/tag_to_course/`, {
      course_ids: courseIds
    })
  }

  async untagResourceFromCourse(resourceId, courseId) {
    return api.post(`${this.baseURL}/saved-resources/${resourceId}/untag_from_course/`, {
      course_id: courseId
    })
  }

  async getResourcesByCourse() {
    return api.get(`${this.baseURL}/saved-resources/by_course/`)
  }

  async getResourcesForCourse(courseId, includeOrg = false) {
    return api.get(`${this.baseURL}/saved-resources/for_course/`, {
      params: { course_id: courseId, include_org: includeOrg }
    })
  }

  async getResourceFolders() {
    return api.get(`${this.baseURL}/saved-resources/folders/`)
  }

  async getResourceStats() {
    return api.get(`${this.baseURL}/saved-resources/stats/`)
  }

  async checkSavedPMIDs(pmids) {
    return api.get(`${this.baseURL}/saved-resources/check_saved/`, {
      params: { pmids: pmids.join(',') }
    })
  }

  async getFavoriteResources() {
    return api.get(`${this.baseURL}/saved-resources/`, {
      params: { favorites: 'true' }
    })
  }

  async getResourcesByFolder(folder) {
    return api.get(`${this.baseURL}/saved-resources/`, {
      params: { folder }
    })
  }

  // External resource integration
  async searchExternalResources(query, resourceType = null) {
    const params = { q: query }
    if (resourceType) {
      params.type = resourceType
    }
    return api.get('/api-integrations/search/', { params })
  }

  async getNCBIArticles(query) {
    return api.get('/api-integrations/ncbi/search/', {
      params: { query }
    })
  }

  async getPubMedArticles(query) {
    return api.get('/api-integrations/pubmed/search/', {
      params: { query }
    })
  }

  // File upload helpers
  async uploadCourseVideo(courseId, videoFile, metadata = {}) {
    const formData = new FormData()
    formData.append('video_file', videoFile)
    formData.append('course', courseId)
    
    Object.keys(metadata).forEach(key => {
      if (metadata[key] !== null && metadata[key] !== undefined) {
        formData.append(key, metadata[key])
      }
    })

    return api.post(`${this.baseURL}/videos/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: metadata.onProgress
    })
  }

  async uploadCourseThumbnail(courseId, thumbnailFile) {
    const formData = new FormData()
    formData.append('thumbnail', thumbnailFile)

    return api.patch(`${this.baseURL}/courses/${courseId}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  async uploadCourseMaterial(videoId, formData, onProgress) {
    return api.post(`${this.baseURL}/videos/${videoId}/upload_material/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(percentCompleted)
        }
      }
    })
  }

  // Vimeo Integration
  async fetchVimeoMetadata(data) {
    return api.post(`${this.baseURL}/videos/fetch_vimeo_metadata/`, data)
  }

  async generateCourseContent(videoId, data) {
    return api.post(`${this.baseURL}/videos/${videoId}/generate_course_content/`, data)
  }

  // Phase 3 — Preview-clip access
  async getVideoPreviewAccess(videoId, seekSeconds = 0, endSeconds = null) {
    const params = { seek: seekSeconds }
    if (endSeconds != null && !Number.isNaN(endSeconds)) {
      params.end = endSeconds
    }
    return api.get(`${this.baseURL}/videos/${videoId}/preview_access/`, {
      params,
    })
  }

  // Video Moment Indexing (Phase 1)
  async generateVideoMoments(videoId) {
    return api.post(`${this.baseURL}/videos/${videoId}/generate_moments/`)
  }

  async getVideoMoments(videoId) {
    return api.get(`${this.baseURL}/videos/${videoId}/moments/`)
  }

  // Moment-level semantic search (Phase 2)
  async momentSearch(query, maxResults = 20) {
    return api.get(`${this.baseURL}/search/moment_search/`, {
      params: { q: query, max_results: maxResults }
    })
  }

  // AI Thumbnail Generation
  async generateCourseThumbnail(courseId) {
    return api.post(`${this.baseURL}/courses/${courseId}/generate-thumbnail/`)
  }

  // ============================================================================
  // RACE Approval Toolkit
  // ============================================================================

  async getRACECourses() {
    return api.get(`${this.baseURL}/race/courses/`)
  }

  async checkRACEReadiness(courseId) {
    return api.post(`${this.baseURL}/race/check-readiness/${courseId}/`)
  }

  async calculateRACEFees(data) {
    return api.post(`${this.baseURL}/race/calculate-fees/`, data)
  }

  async getRACECourseAttachment(courseId) {
    return api.get(`${this.baseURL}/race/course-attachment/${courseId}/`)
  }

  async getRACEAdvertisingLanguage(courseId) {
    return api.get(`${this.baseURL}/race/advertising-language/${courseId}/`)
  }

  async getRACEAttendeeTemplate(courseId) {
    return api.get(`${this.baseURL}/race/attendee-template/${courseId}/`)
  }

  async getRACECertificateRequirements(courseId) {
    return api.get(`${this.baseURL}/race/certificate-requirements/${courseId}/`)
  }

  async updateRACEApplication(courseId, data) {
    return api.post(`${this.baseURL}/race/update-application/${courseId}/`, data)
  }

  async generateRACEPresenterPacket(courseId, data = {}) {
    return api.post(`${this.baseURL}/race/generate-presenter-packet/${courseId}/`, data)
  }

  // Author Content Portal
  async listAuthorSubmissions(params = {}) {
    return api.get(`${this.baseURL}/author-submissions/`, { params })
  }

  async getAuthorSubmission(id) {
    return api.get(`${this.baseURL}/author-submissions/${id}/`)
  }

  async createAuthorSubmission(data) {
    return api.post(`${this.baseURL}/author-submissions/`, data)
  }

  async updateAuthorSubmission(id, data) {
    return api.patch(`${this.baseURL}/author-submissions/${id}/`, data)
  }

  async submitAuthorSubmission(id) {
    return api.post(`${this.baseURL}/author-submissions/${id}/submit/`)
  }

  async runAuthorFactory(id) {
    return api.post(`${this.baseURL}/author-submissions/${id}/run-factory/`)
  }

  async publishAuthorSubmission(id) {
    return api.post(`${this.baseURL}/author-submissions/${id}/publish/`)
  }

  async requestAuthorFixes(id, notes) {
    return api.post(`${this.baseURL}/author-submissions/${id}/request-fixes/`, { notes })
  }

  async evaluateAuthorRubric(id) {
    return api.post(`${this.baseURL}/author-submissions/${id}/evaluate-rubric/`)
  }

  async uploadAuthorAsset(submissionId, formData) {
    return api.post(
      `${this.baseURL}/author-submissions/${submissionId}/upload-asset/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
  }

  // Utility methods
  formatDuration(seconds) {
    if (!seconds) return '0m'
    
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    
    // Format as "1h 23m 45s" for better readability
    const parts = []
    if (hours > 0) parts.push(`${hours}h`)
    if (minutes > 0) parts.push(`${minutes}m`)
    if (secs > 0 && hours === 0) parts.push(`${secs}s`)  // Only show seconds if under 1 hour
    
    return parts.length > 0 ? parts.join(' ') : '0m'
  }

  formatPrice(price) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price)
  }

  formatDate(dateString) {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  formatDateTime(dateString) {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short'
    })
  }
}

// Create and export singleton instance
export const lmsAPI = new LMSAPIService()
export default lmsAPI
